/**
 * Spawner por TERRITÓRIO (10.5M). O mapa é uma grade de pedaços e CADA UM tem sua gangue:
 * os territórios perto do player ficam "ativos" e mantêm N inimigos da tabela daquele pedaço
 * vivos, mais 1 CAMPEÃO (elite) quando o território tem um. Longe do player os inimigos são
 * reciclados (perf). Assim os monstros ficam espalhados pelo mundo inteiro em vez de nascerem
 * num anel ao redor do player.
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import type { EnemyDef } from "../config/GameConfig";
import type { Arena } from "../world/Arena";
import type { Sanctuary } from "../world/Sanctuary";
import type { Territory, TerritoryManager } from "../world/Territories";
import type { EnemyManager } from "./EnemyManager";
import { Enemy } from "./Enemy";
import { enemyRegion } from "./EnemyRegion";
import { randRange } from "../utils/math";
import { quality } from "../core/Quality";

// 10.5I — DERIVADO do config (era um mapa manual: o Void Wisp ficou de fora → def undefined
// → `new Enemy(undefined)` estourava dentro do loop e CONGELAVA o jogo). Agora todo inimigo
// registrado em GameConfig.enemies existe aqui automaticamente.
const ENEMY_BY_ID: Record<string, EnemyDef> = GameConfig.enemies as Record<string, EnemyDef>;
// 10.9HY — era const 10. Agora vem do preset: é o multiplicador que transforma
// `alivePerTerritory` numa demanda de centenas de inimigos (10 × 50 = 500 no medium antigo).
const maxActiveTerritories = (): number => quality.values.activeTerritories;
// 10.9BM — região "dona" de cada monstro (10.9BT: movido para EnemyRegion.ts — o CreatureBuilder
// também usa pra escolher pele/borda por região)

export class EnemySpawner {
  private timer = 0;
  private readonly point = new THREE.Vector3();
  private sanctuary: Sanctuary | null = null;
  private territories: TerritoryManager | null = null;
  // tabela ativa (região): usada como fallback quando não há território
  private table: { id: string; weight: number }[] = GameConfig.spawner.distribution.slice();
  private baseMaxAlive: number = GameConfig.spawner.maxAlive;
  private maxAlive: number = GameConfig.spawner.maxAlive;
  // Rift Surge: modificadores aplicados no spawn (1 = normal)
  private surgeHp = 1;
  private surgeDmg = 1;
  // cooldown de campeão por território (id -> segundos restantes)
  private readonly championCd = new Map<string, number>();
  private readonly active: Territory[] = [];

  constructor(
    private readonly arena: Arena,
    private readonly manager: EnemyManager,
  ) {}

  /** Base segura: nada nasce dentro dela. */
  setSanctuary(sanctuary: Sanctuary): void {
    this.sanctuary = sanctuary;
  }

  /** 10.5M — grade de territórios da região ativa. */
  setTerritories(t: TerritoryManager): void {
    this.territories = t;
    this.championCd.clear();
  }

  /** Define a tabela de inimigos da região ativa (RegionManager) — fallback. */
  setTable(table: { id: string; weight: number }[], maxAlive: number): void {
    this.table = table.slice();
    this.baseMaxAlive = maxAlive;
    this.maxAlive = this.scaledMaxAlive();
  }

  /** 10.9HY — teto de vivos escalado pelo preset (celular fraco não aguenta 220-260 bichos). */
  private scaledMaxAlive(): number {
    return Math.max(12, Math.round(this.baseMaxAlive * quality.values.enemyScale));
  }

  /** 10.9HY — o preset mudou em runtime (Configurações / governador de fps): reaplica o teto. */
  refreshQuality(): void {
    this.maxAlive = this.scaledMaxAlive();
  }

  /** Rift Surge: escala HP/dano dos próximos spawns (§36). */
  setSurge(hpMult: number, dmgMult: number): void {
    this.surgeHp = hpMult;
    this.surgeDmg = dmgMult;
  }

  /** Cria + registra um inimigo, aplicando o Surge ativo. */
  private addEnemy(def: EnemyDef, pos: THREE.Vector3, territoryId: string | null = null, champion = false, tier = 1): Enemy {
    const e = new Enemy(def, this.arena, pos);
    e.territoryId = territoryId;
    e.tier = tier; // 10.9DY — o teto de golpes escala com o tier do territorio
    e.applyTierLook(); // 10.9HK — espinhos/placas/cristais conforme o tier
    e.champion = champion;
    if (this.surgeHp !== 1 || this.surgeDmg !== 1) e.applySurge(this.surgeHp, this.surgeDmg);
    // 10.6F — território tier 2/3 cria bicho de verdade (antes o tier era só enfeite no mapa)
    const ts = GameConfig.territories.tierScale;
    const k = ts[Math.max(1, Math.min(5, tier)) - 1] ?? { hp: 1, dmg: 1 }; // 10.7O: descapado (tier 4/5 valem)
    const reg = enemyRegion(def.id);
    const norm = GameConfig.regionDmgNorm[reg] ?? 1; // 10.9BM — dano normalizado por região
    const hpNorm = GameConfig.regionHpNorm[reg] ?? 1; // 10.9DY — vida normalizada por região
    if (k.hp * hpNorm !== 1 || k.dmg * norm !== 1) e.applyTierScale(k.hp * hpNorm, k.dmg * norm);
    e.capHitsToKill(); // 10.9DT — teto de golpes (vida final nunca vira "1 milhao")
    this.manager.add(e);
    return e;
  }

  update(dt: number, playerPos: THREE.Vector3): void {
    for (const [id, t] of this.championCd) {
      const n = t - dt;
      if (n <= 0) this.championCd.delete(id);
      else this.championCd.set(id, n);
    }
    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer = GameConfig.territories.checkInterval;
    this.recycleFar(playerPos);
    this.fillTerritories(playerPos, 20); // 10.9CS: era 14 — enche mais rápido ao explorar (mapa povoado)
    this.fillSafezoneGarrison(playerPos, 6); // 10.7T: horda perto da base
    this.fillPlayerHorde(playerPos, 6); // 10.9DW: horda ao redor do Caçador em QUALQUER lugar do mapa
  }

  /** Popular o mundo ao iniciar: enche os territórios perto do player de uma vez. */
  prewarm(playerPos: THREE.Vector3): void {
    this.fillTerritories(playerPos, 99);
    this.fillSafezoneGarrison(playerPos, 99);
  }

  /** Territórios ativos = os mais próximos dentro do raio (limitados p/ não lotar o mobile). */
  private activeTerritories(playerPos: THREE.Vector3): Territory[] {
    const tm = this.territories;
    if (!tm) return [];
    tm.activeAround(playerPos.x, playerPos.z, GameConfig.territories.activeRadius, this.active);
    this.active.sort((a, b) => {
      const da = (a.cx - playerPos.x) ** 2 + (a.cz - playerPos.z) ** 2;
      const db = (b.cx - playerPos.x) ** 2 + (b.cz - playerPos.z) ** 2;
      return da - db;
    });
    const maxActive = maxActiveTerritories();
    if (this.active.length > maxActive) this.active.length = maxActive;
    return this.active;
  }

  /** Repõe a gangue (e o campeão) dos territórios ativos, respeitando o teto global. */
  private fillTerritories(playerPos: THREE.Vector3, budget: number): void {
    const terrs = this.activeTerritories(playerPos);
    if (terrs.length === 0) {
      // região sem grade configurada: cai no comportamento antigo (anel ao redor do player)
      if (this.manager.aliveCount < this.maxAlive) this.spawnRing(playerPos);
      return;
    }
    const cfg = GameConfig.territories;
    let spawned = 0;
    for (const t of terrs) {
      if (this.manager.aliveCount >= this.maxAlive || spawned >= budget) return;
      // conta a gangue viva daquele pedaço
      let gang = 0;
      let hasChampion = false;
      for (const e of this.manager.enemies) {
        if (e.isDead || e.territoryId !== t.def.id) continue;
        if (e.champion) hasChampion = true;
        else gang++;
      }
      // campeão do território (elite que lidera a gangue)
      const champId = t.def.champion;
      if (champId && !hasChampion && !this.championCd.has(t.def.id) && ENEMY_BY_ID[champId]) {
        const p = this.pickPointIn(t, playerPos);
        if (p) {
          this.addEnemy(ENEMY_BY_ID[champId], p, t.def.id, true, t.def.tier);
          this.championCd.set(t.def.id, cfg.championRespawn);
          spawned++;
          continue;
        }
      }
      // 10.7T — o território home não é mais penalizado (-2). A densidade perto da base
      // agora é garantida pela GUARNIÇÃO da Safezone (fillSafezoneGarrison).
      // 10.9R — TETO POR TERRITÓRIO: no máximo `perTerr` spawns por tick em CADA pedaço, pra o
      // budget se espalhar (antes os pedaços perto comiam tudo e os ativos longe ficavam vazios).
      const want = Math.max(3, Math.round(cfg.alivePerTerritory * quality.values.enemyScale)); // 10.9HY
      let terrSpawned = 0;
      while (gang < want && terrSpawned < 4 && spawned < budget && this.manager.aliveCount < this.maxAlive) {
        const def = this.pickFromGang(t);
        const p = this.pickPointIn(t, playerPos);
        if (!p) break;
        this.addEnemy(def, p, t.def.id, false, t.def.tier);
        gang++;
        spawned++;
        terrSpawned++;
      }
    }
  }

  /**
   * 10.7T — GUARNIÇÃO DA SAFEZONE. O território "home" é gigante (a Área 1 tem ~138×106m),
   * então N monstros espalhados nele deixam a densidade perto da base baixíssima ("só 3 à
   * vista"). Aqui mantemos uma HORDA viva num anel logo fora da zona segura, ao redor do
   * Santuário — vale para TODAS as áreas (usa o centro do santuário, não o do território).
   * Só roda quando o player está perto da base (início/retorno), pra não gastar teto à toa.
   * Cada monstro é atribuído ao território que contém o ponto (gangue/tier/reciclagem certos).
   */
  private fillSafezoneGarrison(playerPos: THREE.Vector3, budget: number): void {
    const s = this.sanctuary;
    const tm = this.territories;
    if (!s || !tm) return;
    const g = GameConfig.territories.homeGarrison;
    if (playerPos.distanceTo(s.center) > g.activeWithin) return;
    const cx = s.center.x;
    const cz = s.center.z;
    const maxR2 = g.maxR * g.maxR;
    // conta a horda já viva no anel (independente do território dono)
    let inRing = 0;
    for (const e of this.manager.enemies) {
      if (e.isDead || e.removeRequested) continue;
      const dx = e.position.x - cx;
      const dz = e.position.z - cz;
      if (dx * dx + dz * dz <= maxR2) inRing++;
    }
    const minR = s.combatRadius + 2; // nasce logo FORA da zona sem combate (nunca na base)
    const maxR = Math.max(minR + 6, g.maxR);
    let spawned = 0;
    const gWant = Math.max(2, Math.round(g.count * quality.values.enemyScale)); // 10.9HY
    while (inRing < gWant && spawned < budget && this.manager.aliveCount < this.maxAlive) {
      let placed = false;
      for (let i = 0; i < 16; i++) {
        const a = randRange(0, Math.PI * 2);
        const r = randRange(minR, maxR);
        this.point.set(cx + Math.cos(a) * r, 0, cz + Math.sin(a) * r);
        this.arena.clamp(this.point, 2);
        if (s.inNoCombatArea(this.point)) continue;
        const owner = tm.at(this.point.x, this.point.z);
        if (!owner) continue;
        this.addEnemy(this.pickFromGang(owner), this.point.clone(), owner.def.id, false, owner.def.tier);
        inRing++;
        spawned++;
        placed = true;
        break;
      }
      if (!placed) break; // não achou ponto válido — evita loop infinito
    }
  }

  /**
   * 10.9DW — HORDA AO REDOR DO CAÇADOR. Os territórios do campo são enormes (o Covil do Alfa tem
   * 9.578 m²): a gangue de 14 fica a ~28 m um do outro e o Caçador vê 1 monstro num raio de 25 m.
   * Aqui garantimos uma horda viva num anel ao redor dele ONDE ELE ESTIVER — mesma ideia da
   * guarnição da base, agora no mapa inteiro. Cada monstro usa a gangue e o tier do território
   * dono do ponto (nada de bicho errado no pedaço errado).
   */
  private fillPlayerHorde(playerPos: THREE.Vector3, budget: number): void {
    const tm = this.territories;
    if (!tm) return;
    const s = this.sanctuary;
    if (s?.inNoCombatArea(playerPos)) return; // na base quem manda é a guarnição
    const h = GameConfig.territories.playerHorde;
    const maxR2 = h.maxR * h.maxR;
    let near = 0;
    for (const e of this.manager.enemies) {
      if (e.isDead || e.removeRequested) continue;
      const dx = e.position.x - playerPos.x;
      const dz = e.position.z - playerPos.z;
      if (dx * dx + dz * dz <= maxR2) near++;
    }
    let spawned = 0;
    const hWant = Math.max(2, Math.round(h.count * quality.values.enemyScale)); // 10.9HY
    while (near < hWant && spawned < budget && this.manager.aliveCount < this.maxAlive) {
      let placed = false;
      for (let i = 0; i < 16; i++) {
        const a = randRange(0, Math.PI * 2);
        const r = randRange(h.minR, h.maxR);
        this.point.set(playerPos.x + Math.cos(a) * r, 0, playerPos.z + Math.sin(a) * r);
        this.arena.clamp(this.point, 2);
        if (this.point.distanceTo(playerPos) < h.minR) continue; // o clamp pode ter puxado pra perto
        if (s?.inNoCombatArea(this.point)) continue;
        const owner = tm.at(this.point.x, this.point.z);
        if (!owner) continue;
        this.addEnemy(this.pickFromGang(owner), this.point.clone(), owner.def.id, false, owner.def.tier);
        near++;
        spawned++;
        placed = true;
        break;
      }
      if (!placed) break;
    }
  }

  /** Recicla inimigos muito longe do player (não ficam ocupando CPU do outro lado do mapa). */
  private recycleFar(playerPos: THREE.Vector3): void {
    const far = GameConfig.territories.despawnRadius;
    const far2 = far * far;
    for (const e of this.manager.enemies) {
      if (e.isDead || e.removeRequested) continue;
      const dx = e.position.x - playerPos.x;
      const dz = e.position.z - playerPos.z;
      if (dx * dx + dz * dz > far2) e.removeRequested = true;
    }
    this.trimToCap(playerPos);
  }

  /** 10.9HY — quando o TETO baixa (jogador trocou o preset, ou o governador de fps derrubou),
   *  os inimigos que já estavam vivos continuavam no mapa: o alívio só viria aos poucos. Aqui o
   *  excedente é reciclado na hora, começando pelos MAIS DISTANTES (o jogador não vê sumir). */
  private trimToCap(playerPos: THREE.Vector3): void {
    const alive: Enemy[] = [];
    for (const e of this.manager.enemies) {
      if (e.isDead || e.removeRequested) continue;
      if (e.champion || e.zoneAmbient) continue; // campeão e ambiente da Fenda não entram na poda
      alive.push(e);
    }
    const over = alive.length - this.maxAlive;
    if (over <= 0) return;
    alive.sort((a, b) => {
      const da = (a.position.x - playerPos.x) ** 2 + (a.position.z - playerPos.z) ** 2;
      const db = (b.position.x - playerPos.x) ** 2 + (b.position.z - playerPos.z) ** 2;
      return db - da; // mais longe primeiro
    });
    for (let i = 0; i < over; i++) alive[i].removeRequested = true;
  }

  /** 10.9AC — CORROMPIDO do evento Invasão do Vazio: inimigo da região BUFFADO, num anel ao
   * redor do player e TAGUEADO (eventMonster) — cada um morto dá stacks de buff temporário. */
  spawnEventMonster(playerPos: THREE.Vector3, hpMult: number, dmgMult: number, ringMin: number, ringMax: number): Enemy | null {
    for (let i = 0; i < 22; i++) {
      const a = randRange(0, Math.PI * 2);
      const r = randRange(ringMin, ringMax);
      this.point.set(playerPos.x + Math.cos(a) * r, 0, playerPos.z + Math.sin(a) * r);
      this.arena.clamp(this.point, 1.5);
      if (this.sanctuary?.inNoCombatArea(this.point)) continue;
      const e = new Enemy(this.pickEventDef(playerPos), this.arena, this.point.clone());
      e.eventMonster = true;
      e.applySurge(hpMult, dmgMult);
      e.capHitsToKill(); // 10.9DT
      this.manager.add(e);
      return e;
    }
    return null;
  }

  /** Sorteia um inimigo p/ o evento: gangue do território atual, senão tabela da região. */
  private pickEventDef(playerPos: THREE.Vector3): EnemyDef {
    const owner = this.territories?.at(playerPos.x, playerPos.z) ?? null;
    const dist = owner && owner.def.gang.length > 0 ? owner.def.gang : this.table;
    const total = dist.reduce((s, d) => s + d.weight, 0);
    let r = randRange(0, total);
    let picked: EnemyDef | undefined;
    for (const d of dist) { r -= d.weight; if (r <= 0) { picked = ENEMY_BY_ID[d.id]; break; } }
    return picked ?? ENEMY_BY_ID[this.table[0]?.id] ?? GameConfig.enemies.voidling;
  }

  /** Add do Boss (Void Howl): um inimigo leve perto do centro informado. */
  spawnAddNear(center: THREE.Vector3): void {
    const def = Math.random() < 0.5 ? GameConfig.enemies.voidling : GameConfig.enemies.voidRunner;
    const a = randRange(0, Math.PI * 2);
    const r = randRange(4, 8);
    this.point.set(center.x + Math.cos(a) * r, 0, center.z + Math.sin(a) * r);
    this.arena.clamp(this.point, 1.5);
    this.addEnemy(def, this.point.clone());
  }

  /** 10.5L — Chamado de Elite (Loja): invoca o campeão da região perto do player. */
  spawnHuntElite(playerPos: THREE.Vector3, regionId: string): boolean {
    const id = regionId === "shatteredWilds" ? "shardWarden" : regionId === "tempestReach" ? "tempestWarden" : "corruptedBrute";
    const def = ENEMY_BY_ID[id];
    if (!def) return false;
    for (let i = 0; i < 20; i++) {
      const a = randRange(0, Math.PI * 2);
      const r = randRange(9, 16);
      this.point.set(playerPos.x + Math.cos(a) * r, 0, playerPos.z + Math.sin(a) * r);
      this.arena.clamp(this.point, 2.5);
      if (this.sanctuary?.inNoCombatArea(this.point)) continue;
      this.addEnemy(def, this.point.clone(), null, true);
      return true;
    }
    return false;
  }

  /** Nasce um Elite (Corrupted Brute) na faixa do Corrupted Grove. */
  spawnElite(): void {
    const gs = GameConfig.gate.eliteSpawn;
    const x = randRange(-gs.xSpread, gs.xSpread);
    const z = randRange(gs.zMin, gs.zMax);
    this.point.set(x, 0, z);
    this.arena.clamp(this.point, 2);
    this.addEnemy(GameConfig.enemies.corruptedBrute, this.point.clone());
  }

  /** 10.9HX — monstro AMBIENTE da Fenda dos Titãs: ronda os corredores entre as salas. Marcado
   *  com `zoneAmbient` pra NÃO ser varrido junto com os adds que o boss deixou pra trás. */
  spawnZoneAmbient(def: EnemyDef, pos: THREE.Vector3, tier = 2): Enemy {
    const e = this.addEnemy(def, pos, null, false, tier);
    e.zoneAmbient = true;
    return e;
  }

  /** Spawna o Elite da Região 2 (Shard Warden) numa faixa distante. */
  spawnWildsElite(): void {
    const gs = GameConfig.world.wildsElite;
    const x = randRange(-gs.xSpread, gs.xSpread);
    const z = randRange(gs.zMin, gs.zMax);
    this.point.set(x, 0, z);
    this.arena.clamp(this.point, 2.5);
    this.addEnemy(GameConfig.enemies.shardWarden, this.point.clone());
  }

  /** 10.7B — TEST ARENA: 1 de CADA inimigo do jogo, em anel FORA do Santuário.
   * Player usa o pad, é teleportado pra fora, e os monstros aparecem em anel de 18-24m.
   * Skip da checagem sanctuary aqui — o ponto é TESTAR combate. */
  spawnTestArena(centerPos: THREE.Vector3): number {
    const defs = Object.values(ENEMY_BY_ID).filter(d => d && d.id);
    let n = 0;
    for (let i = 0; i < defs.length; i++) {
      const def = defs[i];
      const angle = (Math.PI * 2 * i) / defs.length;
      const r = 12 + (i % 3) * 2; // 3 anéis: 12/14/16 m
      this.point.set(centerPos.x + Math.cos(angle) * r, 0, centerPos.z + Math.sin(angle) * r);
      this.arena.clamp(this.point, 1.5);
      this.addEnemy(def, this.point.clone());
      n++;
    }
    return n;
  }

  /** Spawna o Elite da Região 3 (Tempest Warden) numa faixa distante. */
  spawnTempestElite(): void {
    const gs = GameConfig.world.tempestElite;
    const x = randRange(-gs.xSpread, gs.xSpread);
    const z = randRange(gs.zMin, gs.zMax);
    this.point.set(x, 0, z);
    this.arena.clamp(this.point, 2.5);
    this.addEnemy(GameConfig.enemies.tempestWarden, this.point.clone());
  }

  /** Sorteia um inimigo da GANGUE do território (fallback: tabela da região).
   *  10.9HS — ELITE DE CAMPO: territórios com campeão definido têm uma chance pequena de o
   *  próprio bicho elite nascer na gangue comum (sem o título de campeão — vários podem coexistir
   *  com ele). Thiago: "elites com mais frequência" — antes só existia o ÚNICO campeão vivo por
   *  território; agora o mesmo elite também aparece, mais raro, misturado na horda. */
  private pickFromGang(t: Territory): EnemyDef {
    const champId = t.def.champion;
    if (champId && ENEMY_BY_ID[champId] && Math.random() < GameConfig.territories.fieldEliteChance) {
      return ENEMY_BY_ID[champId];
    }
    const dist = t.def.gang.length > 0 ? t.def.gang : this.table;
    const total = dist.reduce((s, d) => s + d.weight, 0);
    let r = randRange(0, total);
    let picked: EnemyDef | undefined;
    for (const d of dist) {
      r -= d.weight;
      if (r <= 0) { picked = ENEMY_BY_ID[d.id]; break; }
    }
    // guarda: id desconhecido NUNCA pode derrubar o loop — cai no 1º válido
    if (!picked) picked = dist.map((d) => ENEMY_BY_ID[d.id]).find((d) => !!d) ?? GameConfig.enemies.voidling;
    return picked;
  }

  /** Ponto de spawn dentro do território, longe do player e fora da base. */
  private pickPointIn(t: Territory, playerPos: THREE.Vector3): THREE.Vector3 | null {
    const tm = this.territories;
    if (!tm) return null;
    const minD = GameConfig.territories.spawnMinFromPlayer;
    for (let i = 0; i < 18; i++) {
      const p = tm.randomPointIn(t, 3);
      this.point.set(p.x, 0, p.z);
      this.arena.clamp(this.point, 2);
      // 10.6Y — o clamp pode empurrar o ponto pra fora do território (perto da parede da arena);
      // se saiu, tenta de novo em vez de aceitar spawn no lugar errado.
      if (Math.abs(this.point.x - t.cx) > t.halfW - 0.5 || Math.abs(this.point.z - t.cz) > t.halfH - 0.5) continue;
      if (this.sanctuary?.inNoCombatArea(this.point)) continue;
      if (this.point.distanceTo(playerPos) < minD) continue;
      return this.point.clone();
    }
    return null;
  }

  /** Fallback antigo: anel ao redor do player (regiões sem grade de territórios). */
  private spawnRing(playerPos: THREE.Vector3): void {
    const cfg = GameConfig.spawner;
    for (let i = 0; i < 24; i++) {
      const a = randRange(0, Math.PI * 2);
      const r = randRange(cfg.radiusMin, cfg.radiusMax);
      this.point.set(playerPos.x + Math.cos(a) * r, 0, playerPos.z + Math.sin(a) * r);
      this.arena.clamp(this.point, 1.5);
      const inNoCombat = this.sanctuary?.inNoCombatArea(this.point) ?? false;
      if (this.point.distanceTo(playerPos) >= cfg.minDistanceFromPlayer && !inNoCombat) break;
    }
    const dist = this.table;
    const total = dist.reduce((s, d) => s + d.weight, 0);
    let r = randRange(0, total);
    let picked: EnemyDef | undefined;
    for (const d of dist) {
      r -= d.weight;
      if (r <= 0) { picked = ENEMY_BY_ID[d.id]; break; }
    }
    this.addEnemy(picked ?? GameConfig.enemies.voidling, this.point.clone());
  }
}
